import React from 'react';
import cx from 'classnames';

import styles from './ImageSection.module.less';
import sharedStyles from './_imageSection--shared.module.less';
import { bgColorOption } from './types';

const getColorClass = (colorName: string) => {
  if (!colorName) return '';
  return styles[colorName as keyof typeof styles];
};

type ImageSectionProps = {
  /* default is section */
  component?: keyof JSX.IntrinsicElements;
  /** Use the accompanying css class if you need to pass as an Element rather than a string */
  title: JSX.Element | string;
  backgroundColor?: bgColorOption;
  /** Defaults to h2 */
  headerTag?: keyof JSX.IntrinsicElements;
  buttons?: JSX.Element;
  /** This is the section of text below the title.  Use the .bulletsContainer (for BulletedList) and .dek (for a p or text block) shared classes for styling. */
  secondaryContents?: JSX.Element;
  /** Appied to the contianer element, use for addional padding */
  className?: string;
  /** Apply a bottom border on mobile/tablet.  Use css if needed on desktop. */
  borderBottom?: boolean;
  /** Container for the image. Use css to set image size and contents */
  imageContents: JSX.Element;
  /** The side the secondary contents will appear, Defaults to left. */
  textPosition?: 'left' | 'right';
  /** Always display title on top of image */
  titleOnTop?: boolean;
  /** Optional content after buttons */
  children?: JSX.Element;
  isULP?: boolean;
};

const ImageSection: React.FC<ImageSectionProps> = ({
  component: Component = 'section' as keyof JSX.IntrinsicElements,
  title,
  backgroundColor = '' as bgColorOption,
  headerTag: HeaderTag = 'h2' as keyof JSX.IntrinsicElements,
  buttons,
  secondaryContents,
  className,
  borderBottom = false,
  imageContents,
  textPosition = 'left',
  titleOnTop = false,
  children,
  isULP,
}): JSX.Element => {
  const headerPostfix = titleOnTop ? 'Tablet' : 'Mobile';

  return (
    <Component
      className={cx(className, styles.outer, {
        [styles.borderBottom]: borderBottom,
        [getColorClass(backgroundColor)]: backgroundColor,
      })}
    >
      <div
        className={cx(styles.container, {
          [sharedStyles.containerPadding]: !isULP,
          [sharedStyles.ulpContainerPadding]: isULP,
          [styles.textRight]: textPosition === 'right',
        })}
      >
        <div className={cx(styles.textContainer)}>
          <div>
            <HeaderTag
              className={cx(styles.hed, styles[`not${headerPostfix}` as keyof typeof styles])}
            >
              {title}
            </HeaderTag>
            <div>{secondaryContents}</div>
          </div>
          <div className={styles.buttons}>{buttons}</div>
          {children}
        </div>
        <div className={styles.imageContainer}>{imageContents}</div>
        <HeaderTag
          className={cx(styles.hed, styles[`only${headerPostfix}` as keyof typeof styles])}
        >
          {title}
        </HeaderTag>
      </div>
    </Component>
  );
};

export default ImageSection;
